import { put } from 'redux-saga/effects';

import { getLogger } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import {
  commonSelectors,
  createQcsResourceCacheToState,
  createQcsThunks,
  createResourceEntityAdapter,
  qcsResourceShouldPersist,
  qcsResourceStateToCache,
} from '../core/resourceSliceUtils';
import invoiceItemsApi from './invoiceItemsApi';
import type { InvoiceItemsState, InvoiceItemType } from './invoiceItemTypes';
import { INVOICE_ITEMS_REDUCER_KEY } from './invoiceItemTypes';

// Define the initial state using that type
const initialState: InvoiceItemsState = {
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};
const log = getLogger('app-components/flux/invoiceItems/invoiceItemsSlice.ts');

const entityAdapter = createResourceEntityAdapter<InvoiceItemType>();
const entitySelectors = entityAdapter.getSelectors();

export const stateToCacheInvoiceItems = qcsResourceStateToCache;
export const shouldPersistInvoiceItems = qcsResourceShouldPersist;
export const cacheToStateInvoiceItems = createQcsResourceCacheToState(
  entityAdapter,
  entityAdapter.getInitialState(initialState),
);

type InvoiceItemAction = {
  type: string;
  payload: InvoiceItemType[] | undefined;
};

export const slice = createAppSlice({
  name: INVOICE_ITEMS_REDUCER_KEY,
  initialState: entityAdapter.getInitialState(initialState),
  reducers: (create) => ({
    ...createQcsThunks({
      create,
      refetch(dispatch) {
        dispatch(invoiceItemsActions.fetch());
      },
      api: invoiceItemsApi,
      adapter: entityAdapter,
      log,
    }),
    bumpNudgeInvoiceItems: create.reducer((state, { payload }: InvoiceItemAction) => {
      const removeArr: string[] = [];
      const addArr: InvoiceItemType[] = [];
      if (payload) {
        payload.forEach((item) => {
          if (item.isDeleted && item.id) {
            removeArr.push(item.id);
          } else {
            addArr.push(item);
          }
        });

        if (removeArr.length > 0) {
          entityAdapter.removeMany(state, removeArr);
        }
        if (addArr.length > 0) {
          entityAdapter.setMany(state, addArr);
        }
      }
    }),
  }),
  selectors: {
    ...entitySelectors,
    ...commonSelectors,
  },
});

export function* invoiceItemsDumpBump(resources: InvoiceItemType[]) {
  yield put(invoiceItemsActions.bumpNudgeInvoiceItems(resources));
}

export function* invoiceItemsNudgeBump() {
  yield put(invoiceItemsActions.fetch());
}

export const invoiceItemsActions = slice.actions;
export const invoiceItemsSelectors = slice.selectors;
export { slice as invoiceItemsSlice };

export default slice.reducer;
